import React, {FC, useMemo, useState, useEffect, useCallback} from "react"
import notify from "@irati/utils/notify"
import qs from "qs"
import {Redirect, RouteComponentProps} from "react-router-dom"
import {Col, Row} from "@irati/ui/src/Layout"
import {InjectIntl, injectIntl} from "@irati/utils/lang"
import History from "@irati/utils/history"
import * as PropTypes from "prop-types"
import {PartialRoute} from "@irati/utils/history/routing"
import Card from "@irati/ui/src/Layout/Card"
import {EMPTY_RECOGNITION} from "@irati-web/recognition/src/components/Popup/CreateRecognition/Popup"
import {hangPromise} from "@irati/utils/promise"
import YouNeedToMfa from "@irati-web/mfa/components/Error/YouNeedToMfa"
import Loading from "@irati/ui/src/Loading"
import mfaService from "@irati/auth/services/mfa"
import {FormatMessage} from "@irati/utils/lang/types"
import Container from "../styled/Container"
import {StepNames} from "../../enums"
import DistributeRewardStep1 from "../../components/AdminReward/DistributeReward/DistributeRewardStep1"
import DistributeRewardStep2 from "../../components/AdminReward/DistributeReward/DistributeRewardStep2"
import DistributeRewardStep3 from "../../components/AdminReward/DistributeReward/DistributeRewardStep3"
import DistributeRewardSuccessModal from "../../components/AdminReward/DistributeReward/DistributeRewardSuccessModal"
import GenericErrorModal from "../../components/AdminReward/DistributeReward/GenericErrorModal"
import {DistributeRewardsProvider, useRewardsDistribution} from "../../stores/DistributeReward/hooks"
import DistributeRewardSummaryStep2 from "../../components/AdminReward/DistributeReward/DistributeRewardSummaryStep2"
import DistributeRewardSummaryStep1 from "../../components/AdminReward/DistributeReward/DistributeRewardSummaryStep1"

interface Props extends InjectIntl, RouteComponentProps {}

const DistributeReward: FC<Props> = ({formatMessage, match, location: {pathname, search}}, context) => {
  const step = pathname.split("/").at(-1)
  const query = qs.parse(search.replace("?", ""))
  const [mfaLoad, setMfaLoad] = useState(hangPromise<any>())

  useEffect(() => {
    setMfaLoad(mfaService.hasFlag())
  }, [])

  const handleMFARetry = async () => {
    setMfaLoad(mfaService.hasFlag())
  }

  const showNotification = useCallback(
    (...messageInfo: Parameters<FormatMessage>) => {
      notify.success({
        message: formatMessage(...messageInfo)
      })
    },
    [formatMessage]
  )

  const initital = useMemo(() => ({recognition: EMPTY_RECOGNITION(formatMessage), step, search: query}), [step, query, formatMessage])

  return (
    <Loading load={mfaLoad} Error={() => <YouNeedToMfa onRetry={handleMFARetry} />}>
      <PartialRoute exact match={match.path} path="/" component={() => <Redirect to={`/wallet/flexi-assign/${StepNames.Recipients}`} />} />
      <DistributeRewardsProvider initial={initital} showNotification={showNotification}>
        <Container>
          <DistributeRewardComp context={context} />
        </Container>
      </DistributeRewardsProvider>
    </Loading>
  )
}

DistributeReward.contextTypes = {
  appPath: PropTypes.string.isRequired
}

function DistributeRewardComp({context}: {context: any}) {
  const {step} = useRewardsDistribution()

  usePathSync(context)

  const onCSVModalClose = () => History.push("/flexi-reward", context)

  return (
    <>
      <Row>
        <Col md={8}>
          <Card marginBottom={2}>
            {step === "recipients" && <DistributeRewardStep1 />}
            {step === "message" && <DistributeRewardStep2 />}
            {step === "confirm" && <DistributeRewardStep3 />}
          </Card>
        </Col>
        <Col md={4}>
          <Card flush>{step === "confirm" ? <DistributeRewardSummaryStep2 /> : <DistributeRewardSummaryStep1 />}</Card>
        </Col>
      </Row>
      <DistributeRewardSuccessModal onClose={onCSVModalClose} />
      <GenericErrorModal />
    </>
  )
}

export default injectIntl(DistributeReward)

/** keep the url and state in sync */
function usePathSync(context: any) {
  const {step, recipientType: recipient, rewardTemplate} = useRewardsDistribution()
  const template = rewardTemplate?.uuid
  const [currentStep, setCurrentStep] = useState(step)
  const [currentRecipient, setCurrentRecipient] = useState(recipient)
  const [currentTemplate, setCurrentTemplate] = useState(template)

  useEffect(() => {
    if (step !== currentStep || recipient !== currentRecipient || template !== currentTemplate) {
      setCurrentStep(step)
      setCurrentRecipient(recipient)
      setCurrentTemplate(template)
      History.push(`/flexi-assign/${step}?${qs.stringify({template, recipient}, {skipNulls: true})}`, context)
    }
  }, [step, recipient, template, context])
}
